import React, { useCallback } from 'react';

import ClinicalNoteEvaluationHistorySection from './ClinicalNoteEvaluationHistorySection.jsx';
import ClinicalNotePainSection from './ClinicalNotePainSection.jsx';
import ClinicalNoteRomSection from './ClinicalNoteRomSection.jsx';
import ClinicalNoteStrengthSection from './ClinicalNoteStrengthSection.jsx';
import ClinicalNoteGoalSection from './ClinicalNoteGoalSection.jsx';
import ClinicalNoteSpecialTestSection from './ClinicalNoteSpecialTestSection.jsx';
import ClinicalNotePalpationSection from './ClinicalNotePalpationSection.jsx';
import ClinicalNoteContentSection from './ClinicalNoteContentSection.jsx';
import ClinicalNoteAttachedDailySection from './ClinicalNoteAttachedDailySection.jsx';
import ClinicalNoteCollapsibleSection from './ClinicalNoteCollapsibleSection.jsx';
import ClinicalNotePlanOfCareGate from './ClinicalNotePlanOfCareGate.jsx';
import useClinicalSectionStatuses from './useClinicalSectionStatuses.js';

/**
 * Initial Evaluation / Re-Evaluation clinical documentation workspace.
 *
 * Evaluation and Re-Evaluation contain evaluation measurements and narrative,
 * not a standalone treatment editor. Treatment delivered at the same visit is
 * documented independently through the optional Attached Daily Note section.
 *
 * Accordion panels intentionally keep their children mounted. Each editable
 * child must register its own save barrier with navigationCoordinator; passing
 * the prop here does not, by itself, guarantee navigation-safe autosaving.
 *
 * The parent ClinicalNotePage is responsible for the final note workflow,
 * review/signing, and full-page visit navigation.
 */
export default function ClinicalNoteEvaluationForm({
  clinicalNoteId,
  disabled = false,
  onSaved,
  onVersionChange,
  navigationCoordinator = null,
}) {
  const resetKey = String(clinicalNoteId ?? 'new');
  const accordionGroup = `clinical-note-${resetKey}`;
  const { statuses, refreshSectionStatuses } =
    useClinicalSectionStatuses(clinicalNoteId);

  const handleSaved = useCallback(async (...args) => {
    if (typeof onSaved === 'function') {
      onSaved(...args);
    }
    await refreshSectionStatuses();
  }, [onSaved, refreshSectionStatuses]);

  const commonProps = {
    clinicalNoteId,
    disabled,
    onSaved: handleSaved,
    onVersionChange,
    navigationCoordinator,
  };

  // The section sequence also defines adjacent accordion navigation.
  // Do not add standalone Treatment here: it belongs to the attached Daily Note.
  const sections = [
    'evaluation-history',
    'pain',
    'rom',
    'strength',
    'goals',
    'special-tests',
    'palpation',
    'content',
    'plan-of-care',
    'attached-daily',
  ];

  const accordionProps = (key, index) => ({
    sectionKey: `${resetKey}-${key}`,
    accordionGroup,
    previousSectionKey:
      index > 0 ? `${resetKey}-${sections[index - 1]}` : null,
    nextSectionKey:
      index < sections.length - 1
        ? `${resetKey}-${sections[index + 1]}`
        : null,
  });

  return (
    <div className="clinical-note-section-stack">
      <ClinicalNoteCollapsibleSection
        title="Evaluation History"
        defaultCollapsed={false}
        status={statuses.EVALUATION_HISTORY}
        resetKey={`${resetKey}-evaluation-history`}
        {...accordionProps('evaluation-history', 0)}
      >
        <ClinicalNoteEvaluationHistorySection {...commonProps} />
      </ClinicalNoteCollapsibleSection>

      <ClinicalNoteCollapsibleSection
        title="Pain"
        defaultCollapsed
        status={statuses.PAIN}
        resetKey={`${resetKey}-pain`}
        {...accordionProps('pain', 1)}
      >
        <ClinicalNotePainSection {...commonProps} />
      </ClinicalNoteCollapsibleSection>

      <ClinicalNoteCollapsibleSection
        title="Range of Motion"
        defaultCollapsed
        status={statuses.ROM}
        resetKey={`${resetKey}-rom`}
        {...accordionProps('rom', 2)}
      >
        <ClinicalNoteRomSection {...commonProps} />
      </ClinicalNoteCollapsibleSection>

      <ClinicalNoteCollapsibleSection
        title="Strength / GMMT"
        defaultCollapsed
        status={statuses.STRENGTH}
        resetKey={`${resetKey}-strength`}
        {...accordionProps('strength', 3)}
      >
        <ClinicalNoteStrengthSection {...commonProps} />
      </ClinicalNoteCollapsibleSection>

      <ClinicalNoteCollapsibleSection
        title="Goals"
        defaultCollapsed
        status={statuses.GOAL}
        resetKey={`${resetKey}-goals`}
        {...accordionProps('goals', 4)}
      >
        <ClinicalNoteGoalSection {...commonProps} />
      </ClinicalNoteCollapsibleSection>

      <ClinicalNoteCollapsibleSection
        title="Special Tests"
        defaultCollapsed
        status={statuses.SPECIAL_TEST}
        resetKey={`${resetKey}-special-tests`}
        {...accordionProps('special-tests', 5)}
      >
        <ClinicalNoteSpecialTestSection {...commonProps} />
      </ClinicalNoteCollapsibleSection>

      <ClinicalNoteCollapsibleSection
        title="Palpation"
        defaultCollapsed
        status={statuses.PALPATION}
        resetKey={`${resetKey}-palpation`}
        {...accordionProps('palpation', 6)}
      >
        <ClinicalNotePalpationSection {...commonProps} />
      </ClinicalNoteCollapsibleSection>

      <ClinicalNoteCollapsibleSection
        title="Clinical Content / Narrative"
        defaultCollapsed
        status={statuses.CONTENT}
        resetKey={`${resetKey}-content`}
        {...accordionProps('content', 7)}
      >
        <ClinicalNoteContentSection {...commonProps} />
      </ClinicalNoteCollapsibleSection>

      <ClinicalNotePlanOfCareGate
        {...commonProps}
        status={statuses.PLAN_OF_CARE}
        accordionGroup={accordionGroup}
        sectionKey={`${resetKey}-plan-of-care`}
        previousSectionKey={`${resetKey}-content`}
        nextSectionKey={`${resetKey}-attached-daily`}
      />

      <ClinicalNoteCollapsibleSection
        title="Attached Daily Note — Same Visit"
        defaultCollapsed
        status={statuses.ATTACHED_DAILY}
        resetKey={`${resetKey}-attached-daily`}
        {...accordionProps('attached-daily', 9)}
      >
        <ClinicalNoteAttachedDailySection {...commonProps} />
      </ClinicalNoteCollapsibleSection>
    </div>
  );
}
